import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import employeeRequestService from "../../api/employeeRequestService";
import { formatSize } from "./constants";
import { kindOf, previewMimeOf } from "./fileKinds";
import { tint } from "./format";
import AuthImage from "./AuthImage";
import useAttachmentBlob from "./useAttachmentBlob";

const PreviewBody = ({ file }) => {
  const kind = kindOf(file.name);
  const mime = file.isImage ? null : previewMimeOf(file.name);
  const isVideo = mime?.startsWith("video/");
  const isPdf = mime === "application/pdf";
  const blob = useAttachmentBlob(!file.isImage && mime ? file.url : null, mime);

  if (file.isImage) return <AuthImage url={file.url} alt={file.name} />;
  if (isVideo && blob.src) return <video src={blob.src} controls autoPlay={false} />;
  if (isPdf && blob.src) return <iframe src={blob.src} title={file.name} />;

  const Icon = kind.icon;
  return (
    <>
      <Icon size={64} strokeWidth={1.3} color={kind.color} />
      <div style={{ fontSize: 13 }}>
        {mime && !blob.failed ? "Loading preview…" : "No preview for this file type. Download to open it."}
      </div>
    </>
  );
};

/** Full-screen preview of one attachment, with ‹ › navigation between files. */
const AttachmentLightbox = ({ files, index, onIndex, onClose }) => {
  const file = files[index];
  const multi = files.length > 1;
  const go = useCallback((d) => onIndex((index + d + files.length) % files.length), [index, files.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (multi && e.key === "ArrowRight") go(1);
      else if (multi && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, multi, onClose]);

  if (!file) return null;
  const kind = kindOf(file.name);
  const Icon = kind.icon;
  const stop = (e) => e.stopPropagation();

  // Portaled to <body>: the animated panel would otherwise become the containing block of position: fixed.
  return createPortal(
    <div className="er-lb" role="dialog" aria-modal="true" aria-label={file.name} onClick={onClose}>
      <div className="er-lbbar" onClick={stop}>
        <div className="er-lbsq" style={{ width: 34, height: 34 }}><Icon size={17} color={kind.color} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="er-ellipsis" style={{ fontSize: 14, fontWeight: 600 }}>{file.name}</div>
          <div style={{ fontSize: 12, color: "#A8A49E" }}>{kind.kind} · {formatSize(file.size)} · {index + 1} / {files.length}</div>
        </div>
        <button
          type="button"
          className="er-btn"
          onClick={() => employeeRequestService.downloadAttachment(file.url, file.name).catch(() => {})}
          style={{ height: 36, padding: "0 14px", borderRadius: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, background: "var(--grad)", color: "var(--on-accent)" }}
        >
          <Download size={14} strokeWidth={2.2} /> Download
        </button>
        <button type="button" className="er-btn er-lbsq" onClick={onClose} aria-label="Close preview"><X size={16} /></button>
      </div>
      <div className="er-lbstage">
        {multi && (
          <button type="button" className="er-btn er-lbround" onClick={(e) => { stop(e); go(-1); }} aria-label="Previous file"><ChevronLeft size={18} /></button>
        )}
        <div className="er-lbcontent" onClick={stop} style={{ background: `radial-gradient(300px 200px at 50% 40%, ${tint(kind.color, 14)}, transparent 70%), #111113` }}>
          <PreviewBody key={file.id} file={file} />
        </div>
        {multi && (
          <button type="button" className="er-btn er-lbround" onClick={(e) => { stop(e); go(1); }} aria-label="Next file"><ChevronRight size={18} /></button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentLightbox;
